import { action } from '@ember/object';
import { trackedObject } from '@ember/reactive/collections';
import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Otbselfservice } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  OTB_SS_ENTRY_POINTS,
  type OTBSSEntryPoint,
  type OTBSSViaQPValue,
  STAGES,
} from 'lumen/utils/constants/otb-selfservice-constants';

export default class MyotbSsService extends Service {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare localStorage: Services['local-storage'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked otbSelfService?: Otbselfservice;

  // Tracked object to store the state of the OTB SS upsell banner when the user dismisses it
  // Why? When user dismisses a banner, we store this information in localStorage directly.
  // But making changes to the localStorage doesn't trigger reactivity and as a result UI doesn't update accordingly i.e. the banner doesn't disappear.
  // Therefore, we also update this information in a tracked object in order to trigger reactivity.
  bannerVisibilityStates = trackedObject<Record<string, boolean>>();

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get canUserEditOtb() {
    return this.access.hasFeature('otb.selfservice.edit');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isOtbSelfServiceUpsellBannerVisible() {
    return (
      this.showOtbSelfServiceEntryPoints &&
      this.access.hasFeature('otb.empty_placeholder') &&
      !this.access.hasFeature('otb.data_validation')
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get localStorageKey() {
    return `isOtbSelfServiceVisible_${this.hotelAndMarketService.hotel?.id}`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pmsOrOtherSystem() {
    const pmsDatasource = this.otbSelfService?.pmsDatasource;
    const otherSystem = this.otbSelfService?.otherSystem;

    if (otherSystem) {
      return otherSystem;
    }

    return pmsDatasource;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showMarketBenchmarkEnabledView() {
    const wantsToSeeBenchmark = this.otbSelfService?.wantsToSeeBenchmark;
    const stage = this.otbSelfService?.stage;

    // On the active page, when the user is eligible for market OTB and has enabled it, we show a different view.
    // If wantsToSeeBenchmark hasn't been set yet (null), treat it like the user has silently agreed.
    return (
      this.access.hasFeature('otb.market_benchmark_eligible') &&
      (wantsToSeeBenchmark || wantsToSeeBenchmark === null) &&
      stage === STAGES.INTEGRATION_ACTIVE_PAGE
    );
  }

  // These banners can be dismissed by the user. This property indicates if that's the case or not.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showOtbSelfServiceEntryPoints() {
    // If bannerVisibilityStates has the key, return the value from there
    if (this.bannerVisibilityStates[this.localStorageKey]) {
      return this.bannerVisibilityStates[this.localStorageKey];
    }

    // Else, fetch the OTB SS upsell banner state from local storage
    const isOtbSelfServiceVisibleLocalStorage = this.localStorage.getItem(
      this.localStorageKey,
    );
    if (isOtbSelfServiceVisibleLocalStorage) {
      return JSON.parse(isOtbSelfServiceVisibleLocalStorage) as boolean;
    }

    return true;
  }

  @action onOtbSelfServiceButtonClick(
    otbSelfServiceEntryProduct: OTBSSViaQPValue,
    otbSelfServiceEntryPoint: OTBSSEntryPoint,
  ): void {
    this.router.transitionTo('hotel.settings.myotb', {
      queryParams: {
        via: otbSelfServiceEntryProduct,
      },
    });

    const EVENT_TRIGGERS = {
      [OTB_SS_ENTRY_POINTS.RATES_EVOLUTION_CHART]: 'day-detail-modal',
      [OTB_SS_ENTRY_POINTS.RATES_CALENDAR]: 'calendar-view',
    } as const;

    const eventTrigger =
      EVENT_TRIGGERS[otbSelfServiceEntryPoint as keyof typeof EVENT_TRIGGERS] ??
      otbSelfServiceEntryPoint?.toLowerCase().replace(/ /g, '-');

    if (!eventTrigger) {
      return;
    }

    this.usageAnalytics.track('OTB Integrating', {
      // e.g 'Rate Insight' to 'rate-insight'
      product: otbSelfServiceEntryProduct?.toLowerCase().replace(/ /g, '-'),
      // .e.g 'Rates Evolution Chart' to 'rates-evolution-chart'
      eventTrigger,
    });
  }

  // Close all the OTB SS upsell banner entry points when the user clicks Dismiss
  @action onOtbSelfServiceClose(): void {
    this.localStorage.setItem(this.localStorageKey, JSON.stringify(false));

    // Update banner visibility state to trigger reactivity i.e. UI update
    this.bannerVisibilityStates[this.localStorageKey] = false;
  }
}

declare module '@ember/service' {
  interface Registry {
    'myotb-ss': MyotbSsService;
  }
}
